'use client';
import { CaptchaFormMessage } from '@eliadmin/core/src/components/providers/captcha-form-message';

import { forgotPassword } from '@eliadmin/core/src/api/customers/customers-api';
import { ValidationError } from '@eliadmin/core/src/api/errors';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Form } from '@eliadmin/core/src/components/ui/form';
import { useToast } from '@eliadmin/core/src/components/ui/use-toast';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { env } from '@eliadmin/core/src/env';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { zodResolver } from '@hookform/resolvers/zod';
import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { type z } from 'zod';
import { object as __eliadmin_zod_0, string as __eliadmin_zod_1 } from 'zod';
import { ensureZodDefaults as __eliadmin_zod_defaults } from '@eliadmin/core/src/build/zod-defaults';
__eliadmin_zod_defaults();
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Heading } from '../ui/heading';
import { Field } from '@eliadmin/core/src/components/form/field';

export const forgotPasswordSchema = (VM: FormValidationMessages) =>
  __eliadmin_zod_0({
    email: __eliadmin_zod_1().email(VM.EMAIL_INVALID_VALIDATION_MESSAGE()),
  });

export function ForgotPasswordForm() {
  const { t } = useTranslations();

  const { formValidationMessages } = useFormValidationMessages(t);

  const form = useForm<z.infer<ReturnType<typeof forgotPasswordSchema>>>({
    resolver: zodResolver(forgotPasswordSchema(formValidationMessages)),
    defaultValues: {
      email: '',
    },
  });

  const { toast } = useToast();
  const locale = useLocaleStore(s => s.currentLocale);
  const router = useRouter();

  const handleSubmit = form.handleSubmit(async data => {
    try {
      const url = new URL(ROUTES.RESET_PASSWORD(), env.NEXT_PUBLIC_APP_URL);
      await forgotPassword({ ...data, return_url: url.toString(), locale });
      // todo: redirect to login page
      toast({
        title: t('text652'),
        variant: 'success',
      });
      router.push(ROUTES.SIGN_IN());
    } catch (e) {
      // handle 422
      if (e instanceof ValidationError) {
        for (const [field, errors] of Object.entries(e.fields)) {
          form.setError(field as any, { type: 'manual', message: errors.join('\n') });
        }
        return;
      }

      toast({
        title: t('text651'),
        description: t('text3140'),
        variant: 'destructive',
      });
    }
  });

  return (
    <Form {...form}>
        <CaptchaFormMessage errors={form.formState.errors} />
      <form
        className={cn('flex w-full flex-col space-y-6', 'ea-password-reset-form')}
        onSubmit={handleSubmit}
      >
        {/* header */}
        <div className="ea-password-reset-form__header text-center md:text-start">
          <Heading title={t('text188')} tag="h3" />
        </div>

        {/* content */}
        <div className={cn('flex flex-col space-y-4', 'ea-password-reset-form__content')}>
          <Field control={form.control} name="email" label="E-mail" placeholder="E-mail" required />
        </div>

        {/* FOOTER */}
        <div
          className={cn('flex flex-col items-start space-y-4', 'ea-password-reset-form__footer')}
        >
          <Button
            type="submit"
            className={cn('w-full', 'ea-password-reset-form__submit-button')}
            isLoading={form.formState.isSubmitting}
          >
            {t('text185')}
          </Button>
        </div>
      </form>
    </Form>
  );
}
